import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Mail, BookOpen } from "lucide-react";
import { usePageMeta } from "@/lib/seo";

const FIVE = [
  { name: "The Guardian", line: "Holds tight, even when there's finally enough to hold." },
  { name: "The Giver", line: "Generous with everyone's capacity but their own." },
  { name: "The Escapee", line: "Walks away right when it starts working." },
  { name: "The Ghost", line: "Earns, but stays invisible with what they earn." },
  { name: "The Builder", line: "Capacity is ready. One inherited weight is still unset." },
];

export default function Decode() {
  usePageMeta(
    "The Wealth Archetype Decode — ₹299 | Just be you by Mehjabin",
    "Guardian. Giver. Escapee. Ghost. Builder. One of these is quietly running your bank account — find yours in a short, honest decode. Instant delivery."
  );

  return (
    <div className="bg-ink min-h-screen" data-testid="decode-page">
      <div className="max-w-3xl mx-auto px-6 py-16 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.2, 0.7, 0.3, 1] }}
          className="border border-amber/40 rounded-[1.5rem] px-6 py-14 md:px-14 md:py-18 relative overflow-hidden"
        >
          <div className="text-center">
            <div className="text-amber text-[11px] md:text-xs uppercase tracking-[0.28em] font-sans font-semibold mb-6">
              A short, honest decode for people ready to see the pattern
            </div>
            <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-canvas leading-[1.05] tracking-tight">
              The Wealth<br />Archetype Decode
            </h1>
            <p className="font-serif italic text-lg md:text-2xl text-peach-deep mt-6 leading-relaxed">
              One of these is quietly running your bank account —<br className="hidden md:block" /> and it's not the one you'd guess.
            </p>
          </div>

          <div className="mt-12 space-y-3">
            {FIVE.map((a, i) => (
              <motion.div
                key={a.name}
                initial={{ opacity: 0, x: -12 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                className="border border-canvas/10 rounded-2xl px-6 py-4 bg-canvas/[0.03] flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4"
                data-testid={`decode-archetype-${i + 1}`}
              >
                <span className="font-serif text-xl text-canvas whitespace-nowrap">{a.name}</span>
                <span className="font-serif italic text-sm text-amber">{a.line}</span>
              </motion.div>
            ))}
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
            {[
              { icon: BookOpen, t: "19 gilded pages", d: "The tell, the hidden cost & one practice — plus a self-check quiz for each archetype" },
              { icon: Mail, t: "Instant delivery", d: "The PDF lands in your inbox the moment payment completes" },
              { icon: Sparkles, t: "7-day gentle plans", d: "A soft week-long practice plan & 3 journal prompts for every archetype" },
            ].map(({ icon: Icon, t, d }) => (
              <div key={t} className="border border-canvas/10 rounded-2xl p-5 bg-canvas/[0.03]">
                <Icon size={18} className="text-peach-deep mx-auto mb-3" strokeWidth={1.4} />
                <div className="font-sans font-semibold text-sm text-canvas">{t}</div>
                <div className="font-sans text-xs text-canvas/60 mt-1.5 leading-relaxed">{d}</div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <div className="font-serif text-canvas text-3xl mb-1">₹299</div>
            <div className="text-canvas/50 text-xs font-sans mb-6">one-time · yours forever · UPI, cards & netbanking</div>
            <Link
              to="/shop/wealth-archetype-decode"
              className="inline-flex items-center gap-2 bg-peach-deep hover:bg-amber text-ink font-sans font-semibold text-sm uppercase tracking-[0.14em] rounded-full px-10 py-4 transition-colors"
              data-testid="decode-buy-btn"
            >
              Find your archetype <ArrowRight size={16} strokeWidth={2} />
            </Link>
            <p className="text-canvas/40 text-xs font-sans mt-5 max-w-sm mx-auto leading-relaxed">
              The one you resist the hardest is worth a second look. Recognition doesn't always feel like relief.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
